/**
 * Hub access helpers and deprecated nav shims.
 * Nav config lives in workspaceNavigation.ts (vendor, pro, and admin).
 */
import type { UserRole } from '@shopgnd/shared';
import {
  ADMIN_HUB_TITLE,
  buildAdminNav,
  buildWorkspaceNav,
  flatAdminNavItems,
  type WorkspaceNavGroup,
  type WorkspaceNavItem,
} from './workspaceNavigation';

export {
  ADMIN_HUB_TITLE,
  buildAdminNav,
  buildWorkspaceNav,
  flatAdminNavItems,
};

export type HubNavItem = Pick<WorkspaceNavItem, 'to' | 'label' | 'end' | 'itemRoles' | 'icon'>;

export type HubNavGroup = {
  title: string;
  items: HubNavItem[];
};

export type HubNavSection = {
  id: 'admin';
  title: string;
  roles: UserRole[];
  groups: HubNavGroup[];
};

/** @deprecated Nav config moved to workspaceNavigation.ts — use buildAdminNav(). */
export const ADMIN_HUB_SECTION: HubNavSection = {
  id: 'admin',
  title: ADMIN_HUB_TITLE,
  roles: ['admin', 'platform_admin'],
  groups: buildAdminNav(['admin', 'platform_admin']).map((group) => ({
    title: group.title,
    items: group.items.map(({ to, label, end, itemRoles, icon }) => ({
      to,
      label,
      end,
      itemRoles,
      icon,
    })),
  })),
};

/** @deprecated Use ADMIN_HUB_SECTION — kept for imports migrating off HUB_SECTIONS. */
export const HUB_SECTIONS: HubNavSection[] = [ADMIN_HUB_SECTION];

export type HubAccessFlags = {
  hasVendorHub: boolean;
  hasProHub: boolean;
  hasAdminHub: boolean;
};

/** @deprecated Use buildAdminNav() — returns WorkspaceNavGroup[]. */
export function adminHubSectionForRoles(userRoles: UserRole[] = []): HubNavSection | null {
  const groups = buildAdminNav(userRoles);
  if (!groups.length) return null;
  return {
    id: 'admin',
    title: ADMIN_HUB_TITLE,
    roles: ['admin', 'platform_admin'],
    groups: groups.map((group) => ({
      title: group.title,
      items: group.items.map(({ to, label, end, itemRoles, icon }) => ({
        to,
        label,
        end,
        itemRoles,
        icon,
      })),
    })),
  };
}

/** Stable Business Center entry while hub profiles are still loading. */
export function defaultHubPathForAccess(flags: HubAccessFlags): string {
  if (!hasHubAccessFromProfiles(flags)) return '/';
  if (flags.hasAdminHub) return '/hub/admin';
  if (flags.hasVendorHub) return '/hub/home';
  if (flags.hasProHub) return '/hub/pro';
  return '/hub';
}

export function hasHubAccessFromProfiles(flags: HubAccessFlags): boolean {
  return flags.hasVendorHub || flags.hasProHub || flags.hasAdminHub;
}

/** Flat list of all nav items in a section (for search). */
export function flatNavItems(section: HubNavSection): HubNavItem[] {
  return section.groups.flatMap((group) => group.items);
}